// src/components/stories/StoryBubble.tsx
//
// A single circular story bubble: gradient ring when unseen / muted when seen,
// cover (photo, contained logo, or icon), optional PARTNER badge, label beneath.

import { GraduationCap, UserCheck, Handshake, Mail, Calendar, Trophy, Newspaper, Building2, Flame } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Story, StoryIconName } from '@/data/stories/types';

const ICONS: Record<StoryIconName, typeof GraduationCap> = {
  graduation: GraduationCap,
  mentor: UserCheck,
  handshake: Handshake,
  mail: Mail,
  calendar: Calendar,
  trophy: Trophy,
  newspaper: Newspaper,
  building: Building2,
  candle: Flame,
};

interface StoryBubbleProps {
  story: Story;
  seen: boolean;
  isRTL: boolean;
  onOpen: () => void;
}

export function StoryBubble({ story, seen, isRTL, onOpen }: StoryBubbleProps) {
  const lang = isRTL ? 'ar' : 'en';

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group flex w-[76px] shrink-0 snap-start flex-col items-center gap-1.5 focus:outline-none"
      aria-label={story.label[lang]}
    >
      <span className="relative">
        {/* ring */}
        <span
          className={cn(
            'block rounded-full p-[3px] transition-transform duration-200 group-hover:scale-105 group-focus-visible:ring-2 group-focus-visible:ring-primary',
            seen ? 'story-ring-seen' : 'story-ring-unseen',
          )}
        >
          {/* inner gap */}
          <span className="block rounded-full bg-background p-[2px]">
            <StoryCover story={story} />
          </span>
        </span>

        {/* badge */}
        {story.badge && (
          <span
            className="absolute -bottom-1 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-accent px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wide text-accent-foreground shadow"
          >
            {story.badge[lang]}
          </span>
        )}
      </span>

      <span className="line-clamp-2 w-full text-center text-[11px] font-medium leading-tight text-foreground/90">
        {story.label[lang]}
      </span>
    </button>
  );
}

function StoryCover({ story }: { story: Story }) {
  const base = 'h-16 w-16 rounded-full';
  if (story.cover.type === 'image') {
    return <img src={story.cover.value} alt="" loading="lazy" className={cn(base, 'object-cover')} />;
  }
  if (story.cover.type === 'logo') {
    return (
      <span className={cn(base, 'flex items-center justify-center bg-white p-2')}>
        <img src={story.cover.value} alt="" loading="lazy" className="h-full w-full object-contain" />
      </span>
    );
  }
  const Icon = ICONS[story.cover.value];
  // Memorial bubble gets a somber navy backdrop rather than the gold one.
  const somber = story.cover.value === 'candle';
  return (
    <span
      className={cn(
        base,
        'flex items-center justify-center bg-gradient-to-br',
        somber ? 'from-[#16233d] to-[#070138]' : 'from-[#5a3b0a] to-[#241803]',
      )}
    >
      <Icon className={cn('h-7 w-7', somber ? 'text-amber-300' : 'text-accent')} />
    </span>
  );
}
